import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { showAdvertisedFees } from '@/helpers/Fees';
import { groupBy, indexItemsById } from '@/helpers/IndexHelpers';
import { sum } from '@/helpers/Math';
import { getPreloadedPromoCodes } from '@/helpers/PreloadPromoCodes';
import { fetchCartMod } from '@/helpers/PromoCodes';
import { exactlyOneTicket } from '@/helpers/Reserve';
import { getMemberAndGuestStepperGroups, isForFlexibleTickets, isForMembersOnly, } from '@/helpers/TicketGroupHelpers';
import { quantitiesAreEqual, sumQuantities } from '@/helpers/TicketTypeQuantities';
import CitypassCoupons from '@/components/events/CitypassCoupons.vue';
import SelectGroupQuantities from '@/components/events/SelectGroupQuantities.vue';
import FormValidationGuard from '@/components/forms/FormValidationGuard.vue';
import { i18n } from '@/app/I18n';
import { allowMemberReserveBeyondTerm, currentMembership, membershipOnDate, } from '@/state/Membership';
const { min, max } = Math;
/**
 * TODO Split into two components;
 *
 * 1. <SelectQuantities> does not allow quantities to be input by parent component, and would be responsible for;
 *   - Emitting quantities values
 *   - Wrapping <QuantitySteppers>
 *   - Initial quantities
 *   - Passing store anchor to <QuantitySteppers>
 *   - Passing membership limits to <QuantitySteppers>
 *   - Default promo code banner
 *   - CityPASS integration
 *
 * 2. <QuantitySteppers> would be used with v-model as a pure user-input component, and would be responsible for;
 *   - Pure user-input component
 *   - Used with v-model
 *   - Enforcing all limits
 *     - Session capacity
 *     - Overrides on ticket_group configuration; max_tickets_per_order and exclude_from_event_capacity
 *     - Anchor quantities as anchored upsell quantity limits
 *     - Membership
 *   - Handling grouping of ticket groups by members and non-members
 *
 * TODO Come up with better names.
 */
@Component({
    name: 'SelectQuantities',
    components: { FormValidationGuard, CitypassCoupons, SelectGroupQuantities },
})
export default class default_1 extends Vue {
    @Prop({ required: true })
    ticketGroups;
    @Prop()
    selectedSession;
    // Quantities data passed in from the parent.
    @Prop({ required: true })
    value;
    /**
     * Max limit of tickets available.
     *
     * This is used for anchored upsells.
     */
    @Prop()
    limitQuantities;
    @Prop({ required: true })
    eventTemplateId;
    @Prop({ required: true })
    sellerId;
    @Prop({ default: false })
    membersOnly;
    @Prop({ default: false })
    giftOfMembership;
    @Prop()
    showCitypass;
    @Prop()
    citypassCoupons;
    @Prop()
    citypassError;
    @Prop()
    submitting;
    /**
     * @deprecated Only stories & tests should use this, not application code.
     */
    @Prop({ default: 1000 })
    _hardCodedMaxLimit;
    // Internal quantities data.
    internal = {};
    // If a default promo code is set, we may display a banner or show discounted prices.
    // TODO Deprecate banners? Possibly no one uses this feature anymore. How can we check?
    // TODO Move banner support to its own component.
    // `false` indicates that the discount applies to all ticket types.
    discountedTicketTypes = false;
    banners = [];
    created() {
        // TODO Deprecate banners? Possibly no one uses this feature anymore. How can we check?
        // TODO Move banner support to its own component.
        const ticketTypeIds = this.ticketGroups.flatMap((tg) => tg.types.map((tt) => tt.id));
        const promises = getPreloadedPromoCodes().map((code) => fetchCartMod(code, { eventSessionId: this.selectedSession?.id, ticketTypeIds }));
        if (promises.length > 0) {
            Promise.all(promises).then((responses) => {
                const banners = [];
                const types = [];
                for (const response of responses) {
                    banners.push(...response.cartmod._data);
                    types.push(...response.ticket_type._data);
                }
                this.banners = banners;
                this.discountedTicketTypes = groupBy('id', types);
            });
        }
    }
    // TODO Move banner support to its own component.
    get promoCodeBanners() {
        if (!this.showPromoCodeBanners) {
            return [];
        }
        const identifiers = {
            seller: this.sellerId,
            event_template: this.eventTemplateId,
        };
        return this.banners.filter((banner) => {
            const { resource, resource_id } = banner;
            return identifiers[resource] === resource_id;
        });
    }
    // TODO Move banner support to its own component.
    get showPromoCodeBanners() {
        // Only show promo code banners when either;
        //   1. there is a non-free ticket type that this discount applies to, or
        //   2. the discount applies to all ticket types, and there is a non-free ticket type.
        const groups = this.ticketGroups.filter((tg) => tg.types.some((type) => Number(type.currency_amount) > 0));
        if (this.discountedTicketTypes) {
            // Does the discount apply to any of the ticket groups with non-free ticket types?
            return groups.some((tg) => tg.types.some((type) => this.discountedTicketTypes[type.id]));
        }
        else {
            // The discount applies to all ticket types. Is there at least one ticket group with non-free ticket types?
            return groups.length > 0;
        }
    }
    get groups() {
        const discount = (id) => {
            return this.discountedTicketTypes?.[id]?.[0] ?? {};
        };
        const result = this.ticketGroups.map((group) => {
            const types = group.types
                // Ignore types with no quantities.
                .filter((type) => this.propLimit(type) > 0)
                // Merge the discounted prices (ticket types) with undiscounted ticket types.
                // TODO Merge all the discounts for this ticket type, not just the first one. How!? 🤔
                // TODO Document why the discount is merged before the undiscounted ticket type.
                // TODO Deprecate banners? Possibly no one uses this feature anymore. How can we check?
                // TODO Move banner support to its own component.
                .map((type) => ({ ...discount(type.id), ...type }));
            return { ...group, types };
        });
        // Ignore groups with no types.
        return result.filter((group) => group.types.length > 0);
    }
    get memberAndGuestStepperGroups() {
        return getMemberAndGuestStepperGroups(this.groups, this.giftOfMembership, this.memberBenefits);
    }
    isMembersTicketGroup(ticketGroup) {
        return isForMembersOnly(ticketGroup);
    }
    get membership() {
        if (this.selectedSession && !allowMemberReserveBeyondTerm()) {
            return membershipOnDate(this.selectedSession.startTime);
        }
        else {
            return currentMembership();
        }
    }
    get memberBenefits() {
        return this.membership?.benefits;
    }
    /**
     * If the member ticket limits change due to a different membership active
     * for a different date, update quantities
     */
    @Watch('membership')
    onMembershipChange(newMembership, oldMembership) {
        if (newMembership?.id !== oldMembership?.id) {
            this.updateInternalQuantities();
        }
    }
    @Watch('groups', { deep: true })
    onGroupsChange() {
        this.updateInternalQuantities();
    }
    /**
     * Watch the 'value' prop, and update this.internal to the new value.
     */
    @Watch('value', { deep: true, immediate: true })
    onValueChange(value, previous) {
        // Avoid infinite loops by only setting quantities when the value has actually changed.
        if (previous && quantitiesAreEqual(this.internal, value)) {
            // The quantities have not changed.
        }
        else {
            this.updateInternalQuantities();
        }
    }
    updateInternalQuantities() {
        const result = {};
        const available = this.remainingAvailableQuantities;
        for (const group of this.ticketGroups) {
            for (const type of group.types) {
                const current = this.value[type.id] ?? {};
                // Ensure the customer cannot select more tickets than what are available.
                const quantity = min(current.quantity ?? 0, available[group.id][type.id]);
                result[type.id] = {
                    ...current,
                    // Don't allow the quantity to be below 0.
                    quantity: max(quantity, 0),
                };
            }
        }
        this.$set(this, 'internal', result);
    }
    @Watch('internal', { deep: true })
    emitQuantities(quantities) {
        this.$emit('input', { ...quantities });
    }
    onQuantityInput({ ticketType, quantity }) {
        this.internal[ticketType.id].quantity += quantity;
    }
    onPriceInput({ ticketTypeId, price }) {
        this.$set(this.internal[ticketTypeId], 'price', price);
    }
    /**
     * Remaining available ticket quantity for a ticket group's types, accounting for the current selection.
     *
     * "Remaining available" is not the limit. It is the limit, less the currently selected quantity.
     * If any ticket-type stepper is increased to the ticket-group's limit, then all TT steppers in that
     * TG should become disabled.
     */
    get remainingAvailableQuantities() {
        const result = {};
        this.ticketGroups.forEach((group) => {
            const groupAvailability = this.remainingAvailableGroupQuantity(group);
            result[group.id] = {};
            group.types.forEach((type) => {
                const availability = this.remainingAvailableTypeQuantity(group, type);
                // The lower availability applies
                const remainingAvailability = min(availability, groupAvailability);
                // Oversold groups can have negative availability, never let the remaining available be below 0.
                result[group.id][type.id] = max(remainingAvailability, 0);
            });
        });
        return result;
    }
    /**
     * Calculate the remaining tickets left based on memberBenefits and the ticket type's name across all groups where
     * member limits apply. Wildcard member benefits are subtracted once all of a named ticket type benefit is depleted.
     */
    get remainingMemberTicketAvailability() {
        const result = { ...this.memberBenefits };
        const allTypes = this.groups.filter(this.shouldApplyMemberLimits).reduce((types, group) => {
            return types.concat(group.types);
        }, []);
        const typesById = indexItemsById(allTypes);
        for (const id in this.internal) {
            const type = typesById[id];
            if (type) {
                const limit = result[type.name] ?? 0;
                const remaining = limit - this.getQuantity(type);
                result[type.name] = max(remaining, 0);
                if (remaining < 0) {
                    result['*'] += remaining;
                }
            }
        }
        return result;
    }
    getMemberRemaining(type) {
        const availability = this.remainingMemberTicketAvailability;
        return (availability[type.name] ?? 0) + (availability['*'] ?? 0);
    }
    remainingAvailableGroupQuantity(group) {
        const limit = this.groupLimit(group);
        const quantities = group.types.map((type) => this.getQuantity(type));
        const total = sum(quantities);
        return limit - total;
    }
    remainingAvailableTypeQuantity(group, type) {
        const quantity = this.getQuantity(type);
        const typeRemaining = this.typeLimit(type) - quantity;
        const memberRemaining = this.shouldApplyMemberLimits(group) ? this.getMemberRemaining(type) : Infinity;
        return min(typeRemaining, memberRemaining);
    }
    shouldApplyMemberLimits(group) {
        return this.isMembersTicketGroup(group) || this.membersOnly;
    }
    getQuantity(type) {
        return this.internal[type.id]?.quantity || 0;
    }
    /**
     * Max limit of tickets available for a TicketGroup.
     */
    groupLimit(group) {
        const limits = [];
        if (group.max_tickets_per_order >= 0) {
            limits.push(group.max_tickets_per_order);
        }
        if (group.capacity >= 0) {
            const usedCapacity = group.used_capacity ?? 0;
            limits.push(group.capacity + group.oversell_capacity - usedCapacity);
        }
        // Ignore the session limit if the TG is configures not to count towards event capacity.
        // No session has been selected yet when used with <ReserveQuantityFirst>.
        // This limit actually applies to the whole session, not just this group.
        // TODO Introduce an remainingAvailableSessionQuantity move this session capacity limit there.
        if (this.selectedSession && !group.exclude_from_event_capacity) {
            limits.push(this.selectedSession.availableCapacity);
        }
        // The lowest limit applies. With no upper limit.
        return min(Infinity, ...limits);
    }
    typeLimit(type) {
        // Zero is a valid limit for anchored upsells.
        // The lowest limit applies. Never allow limits greater than 1000 (_hardCodedMaxLimit).
        return min(this._hardCodedMaxLimit, this.propLimit(type));
    }
    propLimit(type) {
        if (this.limitQuantities) {
            const item = this.limitQuantities[type.id];
            return item ? item.quantity : 0;
        }
        else {
            return Infinity;
        }
    }
    get showAdvertisedFees() {
        return showAdvertisedFees();
    }
    get exactlyOneTicket() {
        return exactlyOneTicket(this.ticketGroups);
    }
    get isFlexibleTickets() {
        return this.groups.every(isForFlexibleTickets);
    }
    get titleLanguageKey() {
        return this.giftOfMembership ? 'giftOfMembership' : 'default';
    }
    get descriptionLanguageKey() {
        if (this.giftOfMembership) {
            return 'giftOfMembership';
        }
        else if (this.isFlexibleTickets) {
            return 'passes';
        }
        else {
            return 'default';
        }
    }
    get type() {
        return this.isFlexibleTickets ? 'passes' : 'tickets';
    }
    get headerLanguageStrings() {
        return {
            title: i18n.t(`stepperGroupTitles.${this.titleLanguageKey}`, { type: this.type }),
            description: i18n.t(`stepperGroupDescriptions.${this.descriptionLanguageKey}`),
        };
    }
    get hasQuantitiesSelected() {
        return sumQuantities(this.value) > 0;
    }
}
